import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AiVerdict, PaymentStatus, ReviewOrderType } from "@mnp/shared";
import { api } from "./api";

// Mirrors the paymentReview.service shapes. API serializes BigInt → string and
// Date → ISO string (see lib/bigintJson on the API side), so `createdAt` and
// the chat id arrive here as strings.

// «Ozon/WB» удалён из типа 2026-06-12 (архив: ветка archive/ozonwb-flow, тег
// pre-ozonwb-extract). Легаси Ozon/WB-строки могут лежать в очереди до её
// осушения (последний write-путь в архивную таблицу Order — Ozon/WB-ветка
// approvePayment; судьба — карточка PR6) — UI рендерит неизвестный orderType
// нейтрально (raw-строкой), не падает. Единый источник типа — @mnp/shared
// (волна 6, admin-hook-type-mirrors); ре-экспорт сохраняет прежний путь импорта.
export type { ReviewOrderType };

export interface PendingPaymentRow {
  id: string;
  // Widened to string on purpose: см. комментарий у ReviewOrderType выше.
  orderType: ReviewOrderType | (string & {});
  orderId: string;
  clientChatId: string;
  status: PaymentStatus;
  aiVerdict: AiVerdict | null;
  aiPaidAmount: number | null;
  aiTargetAmount: number | null;
  aiMessage: string | null;
  totalAmount: number | null;
  createdAt: string;
}

export interface PaymentDetail extends PendingPaymentRow {
  fileId: string;
  fileType: string;
  /**
   * null until a blob store exists (paymentReview.service keeps the field so
   * this UI is forward-compatible). When non-null the drawer renders the
   * receipt inline.
   */
  fileUrl: string | null;
  aiRaw: unknown;
  orderStatus: string;
  rejectedReason: string | null;
  adminReviewedAt: string | null;
  adminReviewedBy: string | null;
}

export const PENDING_PAYMENTS_QUERY_KEY = "admin-payments-pending";
export const PAYMENT_DETAIL_QUERY_KEY = "admin-payment-detail";

export function usePendingPayments(limit?: number) {
  return useQuery({
    queryKey: [PENDING_PAYMENTS_QUERY_KEY, limit ?? null],
    queryFn: () =>
      api<PendingPaymentRow[]>(
        `/admin/payments/pending${limit ? `?limit=${limit}` : ""}`,
      ),
    refetchInterval: 30_000,
  });
}

export function usePaymentDetail(id: string | null) {
  return useQuery({
    queryKey: [PAYMENT_DETAIL_QUERY_KEY, id],
    queryFn: () => api<PaymentDetail>(`/admin/payments/${id}`),
    enabled: !!id,
  });
}

export function useApprovePayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) =>
      api(`/admin/payments/${id}/approve`, {
        method: "POST",
        body: JSON.stringify(note ? { note } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PENDING_PAYMENTS_QUERY_KEY] });
    },
  });
}

export function useRejectPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api(`/admin/payments/${id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [PENDING_PAYMENTS_QUERY_KEY] });
    },
  });
}
